<template>
  <app-custom-dialog
    v-if="!inline"
    v-model="internalDialog"
    :title="title"
    :width="290"
    :footer="false"
  >
    <div slot="activator">
      <div class="text-center align-center">
        <v-card
          height="60"
          class="display-1 d-flex justify-end align-center px-4 number-card"
        >
          <v-btn
            small
            icon
            @click="onKeyPress({ icon: 'fal fa-times-circle' })"
          >
            <v-icon small> fas fa-times-circle </v-icon>
          </v-btn>
          <v-spacer />

          {{ displayValue | ToNumber }}
        </v-card>
      </div>

      <div class="pb-2">
        <v-row no-gutters>
          <v-col
            class="numeric-pad"
            v-for="(key, index) in keys"
            :key="index"
            cols="4"
          >
            <v-btn tile @click="onKeyPress(key)">
              <v-icon v-if="key.icon">{{ key.icon }}</v-icon>
              <span v-else>{{ key }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-btn color="primary" block depressed @click="submitValue">
        {{ $t(submitText) }}
      </v-btn>
    </div>
  </app-custom-dialog>
  <div v-else>
    <!-- Inline mode: render content without dialog wrapper -->
    <div v-if="!hideCard" class="mb-3">
      <div class="d-flex align-center mb-2">
        <span class="text-capitalize font-weight-bold">{{ $t(title) }}</span>
        <v-spacer />
        <v-icon class="close-btn" @click="closeDialog" color="red" small>
          fal fa-times
        </v-icon>
      </div>
    </div>

    <div class="text-center align-center mb-3">
      <v-card
        height="60"
        class="display-1 d-flex justify-end align-center px-4 number-card"
      >
        <v-btn
          small
          icon
          @click="onKeyPress({ icon: 'fal fa-times-circle' })"
        >
          <v-icon small> fas fa-times-circle </v-icon>
        </v-btn>
        <v-spacer />

        {{ displayValue | ToNumber }}
      </v-card>
    </div>

    <div class="pb-2">
      <v-row no-gutters>
        <v-col
          class="numeric-pad"
          v-for="(key, index) in keys"
          :key="index"
          cols="4"
        >
          <v-btn tile @click="onKeyPress(key)">
            <v-icon v-if="key.icon">{{ key.icon }}</v-icon>
            <span v-else>{{ key }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <div class="mt-3">
      <v-btn color="primary" block @click="submitValue">{{
        $t(submitText)
      }}</v-btn>
    </div>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "NumericPad",
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    submitText: {
      type: String,
      default: "ok",
    },
    dialog: {
      type: Boolean,
      default: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    hideCard: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [myMixin],
  data() {
    return {
      displayValue: this.value || "0",
      keys: [
        "7",
        "8",
        "9",
        "4",
        "5",
        "6",
        "1",
        "2",
        "3",
        ".",
        "0",
        { icon: "fal fa-backspace" },
      ],
    };
  },
  computed: {
    internalDialog: {
      get() {
        // In inline mode, always return true since we don't use dialog wrapper
        if (this.inline) {
          return true;
        }
        return this.dialog;
      },
      set(value) {
        // When dialog closes, emit close event instead of mutating prop
        if (!value && !this.inline) {
          this.$emit("close");
        }
      },
    },
  },

  mounted() {
    this.boundKeyHandler = this.handleKeydown.bind(this);
    window.addEventListener("keydown", this.boundKeyHandler);
  },
  beforeDestroy() {
    if (this.boundKeyHandler) {
      window.removeEventListener("keydown", this.boundKeyHandler);
    }
  },

  methods: {
    handleKeydown(e) {
      // Only handle keys when dialog is open (or in inline mode)
      if (!this.inline && !this.internalDialog) return;

      // Handle numeric keys and backspace
      const numericKeys = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        ".",
      ];

      if (numericKeys.includes(e.key)) {
        e.preventDefault();
        this.onKeyPress(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        this.onKeyPress({ icon: "fal fa-backspace" });
      } else if (e.key === "Enter") {
        e.preventDefault();
        this.submitValue();
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.closeDialog();
      }
      // Don't prevent default for other keys - let them pass through
    },
    onKeyPress(key) {
      if (key.icon === "fal fa-times-circle") {
        this.displayValue = "0";
      } else if (key.icon === "fal fa-backspace") {
        if (this.displayValue.length <= 1) {
          this.displayValue = "0";
        } else {
          this.displayValue = this.displayValue.slice(0, -1);
        }
      } else {
        // if the value is 0, replace it with the key
        if (this.displayValue === "0") {
          this.displayValue = key;
        } else {
          this.displayValue += key;
        }
      }
    },
    submitValue() {
      this.$emit("input", this.displayValue);
      this.closeDialog();
      this.displayValue = "0";
    },
    closeDialog() {
      this.$emit("close");
    },
  },
  watch: {
    value(newVal) {
      this.displayValue = newVal;
    },
  },
};
</script>

<style scoped>
.numeric-pad .v-btn {
  height: 60px;
  width: 100%;
  font-weight: 100;
  font-size: 1.2rem;
  box-shadow: none;
  border-right: 0.1px solid #ccc;
  border-bottom: 0.1px solid #ccc;
}

.numeric-pad {
  border-top: 0.1px solid #ccc;
  border-left: 0.1px solid #ccc;
}
.numeric-pad .row .col button.submit-btn {
  background-color: #5b39c9;
  color: #fff;
}
.numeric-pad .v-btn.theme--dark:hover {
  background-color: #333;
}

.numeric-pad .v-btn.theme--light:hover {
  background-color: #e6e4e4;
}
.number-card.theme--light {
  background-color: #e0e0e0;
}
.number-card.theme--dark {
  background-color: #333;
}
.close-btn {
  cursor: pointer;
  border-radius: 50%;
  width: 30px;
  height: 30px;
}

.close-btn.theme--dark {
  background-color: #333;
}

.close-btn.theme--light {
  background-color: #f5f5f5;
}
</style>
